import { useState } from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { uiNeutrals } from "../../constants/uiNeutrals";
import { closeIconButtonSx } from "../../constants/closeIconButtonSx";
import CustomButton from "../ui/CustomButton";
import { styled } from "@mui/system";
import UserMenu from "../common/UserMenu";

const NavLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  color: "inherit",
  padding: "8px 16px",
  borderRadius: "4px",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
}));

const GLASSY_SHADOW = "0 4px 18px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.65)";

/** Glass effect wrapper — plain Box so click/focus states never touch the shadow. */
const glassyButtonWrapperSx = {
  bgcolor: uiNeutrals.frostedFill,
  backdropFilter: "blur(22px) saturate(1.08)",
  WebkitBackdropFilter: "blur(22px) saturate(1.08)",
  boxShadow: GLASSY_SHADOW,
  border: "1px solid rgba(255,255,255,0.55)",
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
};

/** Fully transparent — purely captures the tap, never affects the wrapper's look. */
const glassyIconButtonSx = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  p: 0,
  color: "#000",
  WebkitTapHighlightColor: "transparent",
  "&&, &&:hover, &&:active, &&:focus, &&:focus-visible, &&.Mui-focusVisible": {
    bgcolor: "transparent",
    boxShadow: "none",
    outline: "none",
  },
};

/** Same layered blur + mask fade as `DealsTabsContainer` fixed header */
const NAV_HEADER_BACKDROP_SX = {
  position: "absolute",
  inset: 0,
  zIndex: 0,
  pointerEvents: "none",
  backdropFilter: "blur(20px) saturate(1.08)",
  WebkitBackdropFilter: "blur(20px) saturate(1.08)",
  maskImage:
    "linear-gradient(to bottom, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 38%, rgba(0,0,0,0.18) 68%, rgba(0,0,0,0) 100%)",
  WebkitMaskImage:
    "linear-gradient(to bottom, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 38%, rgba(0,0,0,0.18) 68%, rgba(0,0,0,0) 100%)",
};

/** Frosted burger + trailing logo (`/home`). Same chrome on Footer-linked marketing routes. */
const HOME_STYLE_NAV_ROUTES = new Set([
  "/home",
  "/about",
  "/contact",
  "/terms",
  "/privacy",
  "/cookies",
  "/disclaimer",
]);

const AUTH_NAV_BACK_ONLY_ROUTES = new Set(["/login", "/signup"]);

const Navbar = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const usesHomeStyleNavChrome = HOME_STYLE_NAV_ROUTES.has(location.pathname);
  const isAuthNavBackOnly = AUTH_NAV_BACK_ONLY_ROUTES.has(location.pathname);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleLoginClick = () => {
    navigate("/login");
  };

  const navLinks = [
    { to: "/home", label: "Home" },
    { to: "/deals", label: "Browse" },
    ...(isAuthenticated ? [] : [{ to: "/login", label: "Login" }]),
  ];

  const openDrawerNavLinksMenu =
    (usesHomeStyleNavChrome || isMobile) && !isAuthNavBackOnly;

  return (
    <>
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          zIndex: 4000,
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          px: { xs: 2, md: 3 },
          pt: {
            xs: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(2)})`,
            md: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(3)})`,
          },
          pb: { xs: 2, md: 3 },
          bgcolor: "transparent",
          overflow: "visible",
        }}
      >
        <Box aria-hidden sx={NAV_HEADER_BACKDROP_SX} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            width: "100%",
            minHeight: 48,
          }}
        >
          {isAuthNavBackOnly ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={glassyButtonWrapperSx}>
                <ArrowBackIcon sx={{ fontSize: 22, pointerEvents: "none" }} />
                <IconButton onClick={() => navigate(-1)} aria-label="Go back" sx={glassyIconButtonSx} />
              </Box>
            </Box>
          ) : usesHomeStyleNavChrome ? (
            <>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <Box sx={glassyButtonWrapperSx}>
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL || ""}/assets/menu.png`}
                    alt=""
                    sx={{ width: 24, height: 24, display: "block", objectFit: "contain", pointerEvents: "none" }}
                  />
                  <IconButton
                    onClick={() => setNavDrawerOpen((open) => !open)}
                    aria-label={navDrawerOpen ? "Close menu" : "Open menu"}
                    sx={glassyIconButtonSx}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Link to="/home">
                  <img
                    src="/assets/logo.png"
                    alt="Loops Logo"
                    style={{ height: 22 }}
                  />
                </Link>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Link to="/home">
                  <img
                    src="/assets/logo.png"
                    alt="Loops Logo"
                    style={{ height: 22 }}
                  />
                </Link>
              </Box>

              {!isMobile ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "1rem",
                  }}
                >
                  {navLinks.map((link) => (
                    <NavLink key={link.to} to={link.to}>
                      {link.label === "Login" ? (
                        <CustomButton
                          onClick={handleLoginClick}
                          sx={{
                            backgroundColor: uiNeutrals.surface,
                            color: "black",
                            "&:hover": {
                              backgroundColor: uiNeutrals.surfaceHover,
                            },
                          }}
                        >
                          Login
                        </CustomButton>
                      ) : (
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: "6px",
                            color: "black",
                            transition: "0.3s",
                            fontSize: isMobile ? "24px" : "14px",
                            "&:hover": {
                              bgcolor: "rgba(255, 255, 255, 0.2)",
                            },
                          }}
                        >
                          {link.label}
                        </Box>
                      )}
                    </NavLink>
                  ))}

                  {isAuthenticated && (
                    <IconButton
                      onClick={() => setUserMenuOpen(true)}
                      sx={{
                        color: "#000",
                        "&:hover": { bgcolor: uiNeutrals.surfaceHover },
                      }}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <IconButton
                    onClick={() => setNavDrawerOpen((open) => !open)}
                    aria-label={navDrawerOpen ? "Close menu" : "More"}
                    sx={
                      navDrawerOpen
                        ? closeIconButtonSx
                        : {
                            color: "black",
                            "&:hover": { bgcolor: uiNeutrals.surfaceHover },
                          }
                    }
                  >
                    {navDrawerOpen ? <CloseIcon /> : <MoreHorizIcon />}
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {openDrawerNavLinksMenu && (
        <UserMenu
          open={navDrawerOpen}
          onClose={() => setNavDrawerOpen(false)}
          navLinks={navLinks}
        />
      )}

      {/* Desktop User Menu */}
      <UserMenu
        open={userMenuOpen}
        onClose={() => setUserMenuOpen(false)}
        navLinks={navLinks}
      />
    </>
  );
};

export default Navbar;
